import { addClass } from 'owa-griffel-utils';
import { getMarkJS, SPAN_ELEMENT_NAME, UNMARK_ELEMENT_LABEL } from './constants';
import createMarkId from './createMarkId';
import type { MarkRegExpOptions, MarkOptions } from 'mark.js';

export type EachElementCallback = (
    keyword: string,
    element: HTMLElement,
    instance?: number
) => void;
export type Accuracy = 'partially' | 'prefix' | 'exactly';

// Escape special regex characters to prevent injection
// From https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// used to bypass loading markjs if no
// search terms are ever passed in
let hasEverMarked = false;

export default async function markElements(
    element: HTMLElement,
    keywords: string[],
    eachCallback: EachElementCallback,
    useRegExp: boolean,
    accuracy: Accuracy,
    doneCallback?: (count: number) => void,
    filter?: (
        textNode: Element,
        foundTerm: string,
        totalCounter: number,
        counter: number
    ) => boolean,
    separateWordSearch?: boolean
): Promise<void> {
    if (keywords.length || hasEverMarked) {
        hasEverMarked = true;
        const MarkJS = await getMarkJS();
        const marker = new MarkJS(element);

        for (const keyword of keywords) {
            let count = 0;

            if (accuracy == 'prefix') {
                const options: MarkRegExpOptions = {
                    element: SPAN_ELEMENT_NAME,
                    className: createMarkId(),
                    // Each element belonging to the same keyword should have the same identifier
                    // This is needed for unmarking user highlights on keywords across elements (VSO20847)
                    acrossElements: true,
                    each: (matchedElement: HTMLElement) => {
                        if (!isEmptyElement(matchedElement)) {
                            eachCallback(keyword, matchedElement, count);
                            count++;
                        }
                    },
                    done: () => {
                        doneCallback?.(count);
                    },
                };

                if (filter) {
                    options.filter = filter;
                }
                marker.markRegExp(new RegExp(`(\\b)(${escapeRegExp(keyword)})`, 'gmi'), options);
            } else {
                const options: MarkOptions = {
                    element: SPAN_ELEMENT_NAME,
                    className: createMarkId(),
                    // Each element belonging to the same keyword should have the same identifier
                    // This is needed for unmarking user highlights on keywords across elements (VSO20847)
                    acrossElements: true,
                    each: (matchedElement: HTMLElement) => {
                        if (!isEmptyElement(matchedElement)) {
                            eachCallback(keyword, matchedElement, count);
                            count++;
                        }
                    },
                    done: () => {
                        doneCallback?.(count);
                    },
                };

                // Only set separateWordSearch if explicitly provided to preserve default behavior
                if (separateWordSearch !== undefined) {
                    options.separateWordSearch = separateWordSearch;
                }

                if (filter) {
                    options.filter = filter;
                }

                if (useRegExp) {
                    marker.markRegExp(new RegExp(escapeRegExp(keyword)), options);
                } else {
                    marker.mark(keyword, options);
                }
            }
        }

        marker.unmark({ className: UNMARK_ELEMENT_LABEL });
    }
}

function isEmptyElement(element: HTMLElement): boolean {
    const content = element.innerHTML.replace(/&nbsp;/gi, '').trim();
    if (!content) {
        // Elements whose content is blank (i.e. breaks, empty table elements,
        // or spans whose content just include whitespace) should not be highlighted
        // Add class name denoting should be removed later
        addClass(element, UNMARK_ELEMENT_LABEL);
        return true;
    }

    return false;
}
